import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Check, ExternalLink, Pencil, ImageIcon, CalendarDays, Search, Mail, Reply } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { CategoryIcon } from "../components/CategoryIcon";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const { t, catLabel } = useLang();
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending");
  const [orgs, setOrgs] = useState([]);
  const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0 });
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [events, setEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [modSearch, setModSearch] = useState("");
  const [messages, setMessages] = useState([]);

  const loadModeration = useCallback(async () => {
    const [g, e, m] = await Promise.all([
      api.get("/admin/gallery"),
      api.get("/admin/events"),
      api.get("/admin/messages"),
    ]);
    setPendingPhotos(g.data);
    setEvents(e.data);
    setMessages(m.data);
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const load = useCallback(async () => {
    const [o, s] = await Promise.all([
      api.get("/admin/organizations", { params: { status: tab } }),
      api.get("/admin/stats"),
    ]);
    setOrgs(o.data);
    setStats(s.data);
  }, [tab]);

  useEffect(() => { if (user) load(); }, [user, load]);
  useEffect(() => { if (user) loadModeration(); }, [user, loadModeration]);

  const approvePhoto = async (id) => { await api.post(`/admin/gallery/${id}/approve`); toast.success("Photo approved"); loadModeration(); };
  const deletePhoto = async (id) => { await api.delete(`/admin/gallery/${id}`); toast.success("Photo removed"); loadModeration(); };
  const approveEvent = async (id) => { await api.post(`/admin/events/${id}/approve`); toast.success("Event approved"); loadModeration(); };
  const deleteEvent = async (id) => { await api.delete(`/admin/events/${id}`); toast.success("Event removed"); loadModeration(); };
  const setMsgStatus = async (id, status) => { await api.put(`/admin/messages/${id}`, { status }); loadModeration(); };
  const deleteMsg = async (id) => { await api.delete(`/admin/messages/${id}`); toast.success("Message deleted"); loadModeration(); };

  const approve = async (id) => { await api.post(`/admin/organizations/${id}/approve`); toast.success("Approved"); load(); };
  const remove = async (id) => { await api.delete(`/admin/organizations/${id}`); toast.success("Deleted"); load(); };

  if (!user) return null;

  const q = modSearch.trim().toLowerCase();
  const filteredPhotos = pendingPhotos.filter((p) => !q || [p.title, p.caption].some((x) => (x || "").toLowerCase().includes(q)));
  const filteredEvents = events.filter((ev) => !q || [ev.title, ev.description, ev.location, ev.organization].some((x) => (x || "").toLowerCase().includes(q)));

  const TABS = [
    { key: "pending", label: t("admin_pending"), count: stats.pending },
    { key: "approved", label: t("admin_approved"), count: stats.approved },
    { key: "all", label: t("admin_all"), count: stats.total },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 fade-up">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-stone-900">{t("admin_title")}</h1>
        <Button variant="outline" size="sm" data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }}
          className="rounded-full gap-2 border-stone-300">
          <LogOut className="w-4 h-4" /> {t("logout")}
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        {TABS.map((tb) => (
          <button key={tb.key} data-testid={`tab-${tb.key}`} onClick={() => setTab(tb.key)}
            className={`rounded-2xl border p-4 text-left transition-colors ${
              tab === tb.key ? "bg-[#2E604A] text-white border-[#2E604A]" : "bg-white border-stone-200 hover:border-[#2E604A]"
            }`}>
            <span className="block text-2xl font-display font-extrabold">{tb.count}</span>
            <span className={`text-sm font-semibold ${tab === tb.key ? "text-white/80" : "text-stone-500"}`}>{tb.label}</span>
          </button>
        ))}
      </div>

      <div className="space-y-3" data-testid="admin-org-list">
        {orgs.length === 0 && <p className="text-center py-16 text-stone-400">No organisations here.</p>}
        {orgs.map((org) => (
          <div key={org._id} data-testid={`admin-org-${org._id}`}
            className="bg-white rounded-2xl border border-stone-200 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h3 className="font-display font-bold text-stone-900">{org.name}</h3>
                <Badge className={`rounded-full border-0 text-[11px] ${org.status === "pending" ? "bg-[#FDF7EB] text-[#B8892F]" : "bg-[#E8EFE9] text-[#2E604A]"}`}>
                  {org.status === "pending" ? t("admin_pending") : t("admin_approved")}
                </Badge>
                {org.notify_status && (org.notify_status === "sent" || org.notify_status === "failed") && (
                  <Badge
                    data-testid={`alert-status-${org._id}`}
                    title={org.notify_error || ""}
                    className={`rounded-full border-0 text-[11px] ${
                      org.notify_status === "sent" ? "bg-[#E8EFE9] text-[#2E604A]" : "bg-[#FDF0ED] text-[#C05E4B]"
                    }`}
                  >
                    {org.notify_status === "sent" ? t("alert_sent") : t("alert_failed")}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-stone-500 line-clamp-1">{org.short_description}</p>
              <div className="flex gap-1.5 mt-1.5 flex-wrap">
                {(org.categories || []).map((c) => (
                  <span key={c} className="text-[11px] font-semibold text-stone-500">#{catLabel(c)}</span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {org.contact?.website && (
                <a href={org.contact.website} target="_blank" rel="noreferrer">
                  <Button variant="ghost" size="icon" className="rounded-full"><ExternalLink className="w-4 h-4" /></Button>
                </a>
              )}
              <Button variant="outline" size="icon" data-testid={`edit-${org._id}`} onClick={() => setEditing(org)}
                className="rounded-full border-stone-300 hover:border-[#2E604A] hover:text-[#2E604A]">
                <Pencil className="w-4 h-4" />
              </Button>
              {org.status === "pending" && (
                <Button size="sm" data-testid={`approve-${org._id}`} onClick={() => approve(org._id)}
                  className="rounded-full bg-[#2E604A] hover:bg-[#234B3A] gap-1.5">
                  <Check className="w-4 h-4" /> {t("approve")}
                </Button>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="icon" data-testid={`delete-${org._id}`} className="rounded-full border-[#C05E4B]/40 text-[#C05E4B] hover:bg-[#FDF0ED]">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-2xl">
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("delete")} — {org.name}?</AlertDialogTitle>
                    <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-full">{t("cancel")}</AlertDialogCancel>
                    <AlertDialogAction data-testid={`confirm-delete-${org._id}`} onClick={() => remove(org._id)}
                      className="rounded-full bg-[#C05E4B] hover:bg-[#A65140]">{t("delete")}</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
      </div>

      {/* Content moderation */}
      <div className="mt-12" data-testid="moderation-section">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <h2 className="font-display text-2xl font-extrabold text-stone-900">{t("mod_title")}</h2>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              data-testid="mod-search"
              value={modSearch}
              onChange={(e) => setModSearch(e.target.value)}
              placeholder={t("mod_search")}
              className="h-10 ps-10 rounded-full bg-white border-stone-200 text-sm"
            />
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Photos management */}
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-8 h-8 rounded-lg bg-[#E8EFE9] text-[#2E604A] flex items-center justify-center"><ImageIcon className="w-4 h-4" /></span>
              <h3 className="font-display font-bold text-stone-900">{t("mod_photos")}</h3>
              <Badge className="rounded-full border-0 bg-[#FDF7EB] text-[#B8892F] text-[11px]">{pendingPhotos.filter((p) => p.status === "pending").length}</Badge>
            </div>
            {pendingPhotos.length === 0 ? (
              <p className="text-sm text-stone-400 py-6 text-center">{t("mod_none")}</p>
            ) : (
              <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1" data-testid="admin-photos">
                {filteredPhotos.map((p) => (
                  <div key={p.id} data-testid={`admin-photo-${p.id}`} className="flex items-center gap-3 rounded-xl border border-stone-100 p-2">
                    <img src={mediaUrl(p.image_url)} alt={p.title} loading="lazy" className="w-16 h-16 rounded-lg object-cover shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm text-stone-900 truncate">{p.title}</p>
                        {p.status === "pending" && <Badge className="rounded-full border-0 bg-[#FDF7EB] text-[#B8892F] text-[10px]">{t("admin_pending")}</Badge>}
                      </div>
                      <p className="text-xs text-stone-500 line-clamp-2">{p.caption}</p>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <Button size="icon" variant="outline" data-testid={`edit-photo-${p.id}`} onClick={() => setEditingPhoto(p)} className="rounded-full border-stone-300 hover:border-[#2E604A] hover:text-[#2E604A] h-9 w-9"><Pencil className="w-4 h-4" /></Button>
                      {p.status === "pending" && (
                        <Button size="icon" data-testid={`approve-photo-${p.id}`} onClick={() => approvePhoto(p.id)} className="rounded-full bg-[#2E604A] hover:bg-[#234B3A] h-9 w-9"><Check className="w-4 h-4" /></Button>
                      )}
                      <Button size="icon" variant="outline" data-testid={`delete-photo-${p.id}`} onClick={() => deletePhoto(p.id)} className="rounded-full border-[#C05E4B]/40 text-[#C05E4B] hover:bg-[#FDF0ED] h-9 w-9"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Events management */}
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-8 h-8 rounded-lg bg-[#FDF0ED] text-[#C05E4B] flex items-center justify-center"><CalendarDays className="w-4 h-4" /></span>
              <h3 className="font-display font-bold text-stone-900">{t("mod_events")}</h3>
              <Badge className="rounded-full border-0 bg-[#FDF7EB] text-[#B8892F] text-[11px]">{events.filter((e) => e.status === "pending").length}</Badge>
            </div>
            {events.length === 0 ? (
              <p className="text-sm text-stone-400 py-6 text-center">{t("mod_none")}</p>
            ) : (
              <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1" data-testid="admin-events">
                {filteredEvents.map((ev) => (
                  <div key={ev.id} data-testid={`admin-event-${ev.id}`} className="flex items-center gap-3 rounded-xl border border-stone-100 p-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm text-stone-900 truncate">{ev.title}</p>
                        {ev.status === "pending" && <Badge className="rounded-full border-0 bg-[#FDF7EB] text-[#B8892F] text-[10px]">{t("admin_pending")}</Badge>}
                      </div>
                      <p className="text-xs text-stone-500">{ev.date}{ev.time ? ` · ${ev.time}` : ""}{ev.location ? ` · ${ev.location}` : ""}</p>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <Button size="icon" variant="outline" data-testid={`edit-event-${ev.id}`} onClick={() => setEditingEvent(ev)} className="rounded-full border-stone-300 hover:border-[#2E604A] hover:text-[#2E604A] h-9 w-9"><Pencil className="w-4 h-4" /></Button>
                      {ev.status === "pending" && (
                        <Button size="icon" data-testid={`approve-event-${ev.id}`} onClick={() => approveEvent(ev.id)} className="rounded-full bg-[#2E604A] hover:bg-[#234B3A] h-9 w-9"><Check className="w-4 h-4" /></Button>
                      )}
                      <Button size="icon" variant="outline" data-testid={`delete-event-${ev.id}`} onClick={() => deleteEvent(ev.id)} className="rounded-full border-[#C05E4B]/40 text-[#C05E4B] hover:bg-[#FDF0ED] h-9 w-9"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="mt-12" data-testid="messages-section">
        <div className="flex items-center gap-2 mb-5">
          <span className="w-8 h-8 rounded-lg bg-[#E8EFE9] text-[#2E604A] flex items-center justify-center"><Mail className="w-4 h-4" /></span>
          <h2 className="font-display text-2xl font-extrabold text-stone-900">{t("msg_title")}</h2>
          <Badge className="rounded-full border-0 bg-[#FDF7EB] text-[#B8892F] text-[11px]">{messages.filter((m) => m.status === "new").length}</Badge>
        </div>
        {messages.length === 0 ? (
          <p className="text-sm text-stone-400 py-8 text-center bg-white rounded-2xl border border-stone-200">{t("msg_none")}</p>
        ) : (
          <div className="space-y-3" data-testid="messages-list">
            {messages.map((m) => (
              <div key={m.id} data-testid={`message-${m.id}`}
                className="bg-white rounded-2xl border border-stone-200 shadow-sm p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-display font-bold text-stone-900">{m.name}</p>
                      <a href={`mailto:${m.email}`} className="text-sm text-[#2E604A] hover:underline">{m.email}</a>
                      <Badge className={`rounded-full border-0 text-[10px] ${m.status === "new" ? "bg-[#FDF7EB] text-[#B8892F]" : m.status === "replied" ? "bg-[#E8EFE9] text-[#2E604A]" : "bg-stone-100 text-stone-500"}`}>
                        {m.status === "new" ? t("msg_new") : m.status === "replied" ? t("msg_replied") : t("msg_read")}
                      </Badge>
                    </div>
                    <p className="text-xs text-stone-400 mt-0.5">{new Date(m.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <a href={`mailto:${m.email}?subject=${encodeURIComponent("Re: your message to Auckland Refugee Support")}&body=${encodeURIComponent("\n\n---\nYour message:\n" + m.message)}`}
                      onClick={() => setMsgStatus(m.id, "replied")} data-testid={`reply-${m.id}`}>
                      <Button size="sm" className="rounded-full bg-[#2E604A] hover:bg-[#234B3A] gap-1.5 h-9"><Reply className="w-4 h-4" /> {t("msg_reply")}</Button>
                    </a>
                    {m.status === "new" && (
                      <Button size="sm" variant="outline" data-testid={`read-${m.id}`} onClick={() => setMsgStatus(m.id, "read")} className="rounded-full border-stone-300 h-9">{t("msg_mark_read")}</Button>
                    )}
                    <Button size="icon" variant="outline" data-testid={`delete-msg-${m.id}`} onClick={() => deleteMsg(m.id)} className="rounded-full border-[#C05E4B]/40 text-[#C05E4B] hover:bg-[#FDF0ED] h-9 w-9"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
                <p className="text-sm text-stone-700 mt-3 leading-relaxed whitespace-pre-wrap border-t border-stone-100 pt-3">{m.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>


      {editingEvent && (
        <EventEditDialog
          event={editingEvent}
          categories={categories}
          onClose={() => setEditingEvent(null)}
          onSaved={() => { setEditingEvent(null); loadModeration(); }}
        />
      )}

      {editingPhoto && (
        <PhotoEditDialog
          photo={editingPhoto}
          onClose={() => setEditingPhoto(null)}
          onSaved={() => { setEditingPhoto(null); loadModeration(); }}
        />
      )}

      {editing && (
        <EditDialog
          org={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function EditDialog({ org, categories, onClose, onSaved }) {
  const { t, catLabel } = useLang();
  const c = org.contact || {};
  const [form, setForm] = useState({
    name: org.name, short_description: org.short_description,
    services: (org.services || []).join("\n"),
    phone: c.phone || "", freephone: c.freephone || "", email: c.email || "",
    website: c.website || "", address: c.address || "", featured: !!org.featured,
  });
  const [selected, setSelected] = useState(org.categories || []);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (cat) => setSelected((s) => (s.includes(cat) ? s.filter((x) => x !== cat) : [...s, cat]));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/organizations/${org._id}`, {
        name: form.name,
        short_description: form.short_description,
        services: form.services.split("\n").map((s) => s.trim()).filter(Boolean),
        categories: selected,
        featured: form.featured,
        contact: { phone: form.phone, freephone: form.freephone, email: form.email, website: form.website, address: form.address },
      });
      toast.success(t("saved"));
      onSaved();
    } catch {
      toast.error("Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-dialog">
        <DialogHeader><DialogTitle className="font-display text-start">{t("edit_title")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <F label={t("f_name")}><Input data-testid="edit-name" value={form.name} onChange={set("name")} className="rounded-xl h-11" /></F>
          <F label={t("f_desc")}><Textarea data-testid="edit-desc" value={form.short_description} onChange={set("short_description")} rows={3} className="rounded-xl" /></F>
          <div>
            <Label className="text-sm font-semibold mb-2 block">{t("f_cats")}</Label>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button type="button" key={cat} data-testid={`edit-cat-${cat}`} onClick={() => toggle(cat)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                    selected.includes(cat) ? "bg-[#2E604A] text-white border-[#2E604A]" : "bg-white text-stone-600 border-stone-300"
                  }`}>
                  <CategoryIcon category={cat} className="w-3.5 h-3.5" /> {catLabel(cat)}
                </button>
              ))}
            </div>
          </div>
          <F label={t("f_services")}><Textarea data-testid="edit-services" value={form.services} onChange={set("services")} rows={4} className="rounded-xl" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label={t("f_phone")}><Input data-testid="edit-phone" value={form.phone} onChange={set("phone")} className="rounded-xl h-10" /></F>
            <F label="Freephone"><Input data-testid="edit-freephone" value={form.freephone} onChange={set("freephone")} className="rounded-xl h-10" /></F>
            <F label={t("f_email")}><Input data-testid="edit-email" value={form.email} onChange={set("email")} className="rounded-xl h-10" /></F>
            <F label={t("f_website")}><Input data-testid="edit-website" value={form.website} onChange={set("website")} className="rounded-xl h-10" /></F>
          </div>
          <F label={t("f_address")}><Input data-testid="edit-address" value={form.address} onChange={set("address")} className="rounded-xl h-10" /></F>
          <div className="flex items-center justify-between rounded-xl border border-stone-200 p-3">
            <Label className="text-sm font-semibold">{t("featured")}</Label>
            <Switch data-testid="edit-featured" checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full">{t("cancel")}</Button>
          <Button data-testid="edit-save" onClick={save} disabled={saving} className="rounded-full bg-[#2E604A] hover:bg-[#234B3A]">{t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const F = ({ label, children }) => (
  <div>
    <Label className="text-sm font-semibold mb-1.5 block">{label}</Label>
    {children}
  </div>
);

function PhotoEditDialog({ photo, onClose, onSaved }) {
  const { t } = useLang();
  const [title, setTitle] = useState(photo.title || "");
  const [caption, setCaption] = useState(photo.caption || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/gallery/${photo.id}`, { title, caption });
      toast.success(t("saved"));
      onSaved();
    } catch {
      toast.error("Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl" data-testid="photo-edit-dialog">
        <DialogHeader><DialogTitle className="font-display text-start">{t("edit")} — {t("nav_gallery")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <img src={mediaUrl(photo.image_url)} alt={title} className="w-full h-44 object-cover rounded-xl" />
          <F label={t("g_title_label")}><Input data-testid="edit-photo-title" value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-xl h-11" /></F>
          <F label={t("g_caption_label")}><Textarea data-testid="edit-photo-caption" value={caption} onChange={(e) => setCaption(e.target.value)} rows={2} className="rounded-xl" /></F>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full">{t("cancel")}</Button>
          <Button data-testid="edit-photo-save" onClick={save} disabled={saving} className="rounded-full bg-[#2E604A] hover:bg-[#234B3A]">{t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EventEditDialog({ event, categories, onClose, onSaved }) {
  const { t, catLabel } = useLang();
  const [form, setForm] = useState({
    title: event.title || "", description: event.description || "", date: (event.date || "").slice(0, 10),
    time: event.time || "", location: event.location || "", organization: event.organization || "",
    category: event.category || "community",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.title || !form.date) { toast.error("Please add a title and date."); return; }
    setSaving(true);
    try {
      await api.put(`/admin/events/${event.id}`, form);
      toast.success(t("saved"));
      onSaved();
    } catch {
      toast.error("Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="event-edit-dialog">
        <DialogHeader><DialogTitle className="font-display text-start">{t("edit")} — {t("nav_events")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <F label={t("ev_title_label")}><Input data-testid="edit-event-title" value={form.title} onChange={set("title")} className="rounded-xl h-11" /></F>
          <F label={t("ev_desc_label")}><Textarea data-testid="edit-event-desc" value={form.description} onChange={set("description")} rows={3} className="rounded-xl" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label={t("ev_date_label")}><Input data-testid="edit-event-date" type="date" value={form.date} onChange={set("date")} className="rounded-xl h-11" /></F>
            <F label={t("ev_time_label")}><Input data-testid="edit-event-time" value={form.time} onChange={set("time")} className="rounded-xl h-11" /></F>
          </div>
          <F label={t("ev_location_label")}><Input data-testid="edit-event-location" value={form.location} onChange={set("location")} className="rounded-xl h-11" /></F>
          <F label={t("ev_org_label")}><Input data-testid="edit-event-org" value={form.organization} onChange={set("organization")} className="rounded-xl h-11" /></F>
          <F label={t("ev_cat_label")}>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="edit-event-category" className="rounded-xl h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}</SelectContent>
            </Select>
          </F>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full">{t("cancel")}</Button>
          <Button data-testid="edit-event-save" onClick={save} disabled={saving} className="rounded-full bg-[#2E604A] hover:bg-[#234B3A]">{t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
